import {
  AloftArea,
  AloftPrice,
  LocationType,
  ManualAdjustmentKey,
  isDefined,
  orUndefined,
  schAlertType,
  schAloftArea,
  schAloftPrice,
  schLocationType,
  schPairedCostRouteItem,
} from '@aloft/toolkit-shared';

import { PropertyGridCellKey } from '../../../../components/PropertyGrid/types.ts';
import { CompAdjustments } from '../../../../redux/features/selectors/CompAdjustments.ts';
import { CompPropertyDataState, SubjectPropertyDataState } from '../../../../redux/types.ts';
import { AlertDefinition } from '../../types.ts';
import { getAlertTriggers } from '../utils/alertTriggers.ts';
import {
  isConditionAdjustmentMaterial,
  isGlaAdjustmentMaterial,
  isQualityAdjustmentMaterial,
  isSiteSizeAdjustmentMaterial,
} from '../utils/compAdjustmentMateriality.ts';
import { getComparableDisplayName, getScopedAlertType } from '../utils/compTypeScoping.ts';
import { getSubjectDataForCompType } from '../utils/getSubjectDataForCompType.ts';

type FeatureComparison = 'greater' | 'lesser' | 'older' | 'newer';

type AlertCopyContext = {
  propertyName: string;
  comparison: FeatureComparison;
  adjustmentDirection: string;
  subjectValue: number | AloftArea;
  compValue: number | AloftArea;
  label: string;
};

type FeatureCheck = {
  label: string;
  adjustmentCellKeyId: ManualAdjustmentKey;
  isMaterialDifference: (params: {
    subjectValue: number | AloftArea;
    compValue: number | AloftArea;
    locationType?: LocationType;
  }) => boolean;
  getWrongDirectionComparison: (
    adjustment: AloftPrice,
    compValue: number | AloftArea,
    subjectValue: number | AloftArea,
  ) => FeatureComparison | undefined;
  getSubjectValue: (
    subject: SubjectPropertyDataState['subjectData']['editableData'],
  ) => number | AloftArea | undefined;
  getCompValue: (
    comp: CompPropertyDataState['compData']['editableData'],
  ) => number | AloftArea | undefined;
  getAdjustment: (compAdjustments: CompAdjustments) => AloftPrice | undefined;
  buildAlertCopy?: (ctx: AlertCopyContext) => { message: string; tips: string };
};

const glaFeatureCheck: FeatureCheck = {
  label: 'GLA',
  adjustmentCellKeyId: 'gla',
  isMaterialDifference: ({ subjectValue, compValue }) =>
    isGlaAdjustmentMaterial(subjectValue as AloftArea, compValue as AloftArea),
  getSubjectValue: (subject) => orUndefined(schAloftArea.parse)(subject.improvements?.gla),
  getCompValue: (comp) => orUndefined(schAloftArea.parse)(comp.improvements?.gla),
  getAdjustment: (compAdjustments) =>
    orUndefined(schAloftPrice.parse)(compAdjustments.mainDwelling?.gla?.adjustment),
  getWrongDirectionComparison: (adjustment, compValue, subjectValue) => {
    const compGla = compValue as AloftArea;
    const subjectGla = subjectValue as AloftArea;

    if (adjustment.gt(AloftPrice.zero) && compGla.gt(subjectGla)) return 'greater';
    if (adjustment.lt(AloftPrice.zero) && compGla.lt(subjectGla)) return 'lesser';
    return undefined;
  },
};

const siteSizeFeatureCheck: FeatureCheck = {
  label: 'Site Size',
  adjustmentCellKeyId: 'site',
  isMaterialDifference: ({ subjectValue, compValue, locationType }) =>
    isSiteSizeAdjustmentMaterial(subjectValue as AloftArea, compValue as AloftArea, locationType),
  getSubjectValue: (subject) => orUndefined(schAloftArea.parse)(subject.site?.area),
  getCompValue: (comp) => orUndefined(schAloftArea.parse)(comp.site?.area),
  getAdjustment: (compAdjustments) =>
    orUndefined(schAloftPrice.parse)(compAdjustments.site?.size?.adjustment),
  getWrongDirectionComparison: (adjustment, compValue, subjectValue) => {
    const compArea = compValue as AloftArea;
    const subjectArea = subjectValue as AloftArea;

    if (adjustment.gt(AloftPrice.zero) && compArea.ge(subjectArea)) return 'greater';
    if (adjustment.lt(AloftPrice.zero) && compArea.le(subjectArea)) return 'lesser';
    return undefined;
  },
};

const getSixTierWrongDirectionComparison: FeatureCheck['getWrongDirectionComparison'] = (
  adjustment,
  compValue,
  subjectValue,
) => {
  const compRating = compValue as number;
  const subjectRating = subjectValue as number;

  if (adjustment.gt(AloftPrice.zero) && compRating < subjectRating) return 'lesser';
  if (adjustment.lt(AloftPrice.zero) && compRating > subjectRating) return 'greater';
  return undefined;
};

const conditionFeatureCheck: FeatureCheck = {
  label: 'Condition',
  adjustmentCellKeyId: 'condition',
  isMaterialDifference: ({ subjectValue, compValue }) =>
    isConditionAdjustmentMaterial(subjectValue as number, compValue as number),
  getSubjectValue: (subject) => {
    const value = subject.improvements?.uadCondition;
    return isDefined(value) ? value : undefined;
  },
  getCompValue: (comp) => {
    const value = comp.improvements?.uadCondition;
    return isDefined(value) ? value : undefined;
  },
  getAdjustment: (compAdjustments) =>
    orUndefined(schAloftPrice.parse)(compAdjustments.condition?.adjustment),
  getWrongDirectionComparison: getSixTierWrongDirectionComparison,
};

const qualityFeatureCheck: FeatureCheck = {
  label: 'Quality',
  adjustmentCellKeyId: 'quality',
  isMaterialDifference: ({ subjectValue, compValue }) =>
    isQualityAdjustmentMaterial(subjectValue as number, compValue as number),
  getSubjectValue: (subject) => {
    const value = subject.improvements?.uadQuality;
    return isDefined(value) ? value : undefined;
  },
  getCompValue: (comp) => {
    const value = comp.improvements?.uadQuality;
    return isDefined(value) ? value : undefined;
  },
  getAdjustment: (compAdjustments) =>
    orUndefined(schAloftPrice.parse)(compAdjustments.quality?.adjustment),
  getWrongDirectionComparison: getSixTierWrongDirectionComparison,
};

const getCountWrongDirectionComparison: FeatureCheck['getWrongDirectionComparison'] = (
  adjustment,
  compValue,
  subjectValue,
) => {
  const compCount = compValue as number;
  const subjectCount = subjectValue as number;

  if (adjustment.gt(AloftPrice.zero) && compCount > subjectCount) return 'greater';
  if (adjustment.lt(AloftPrice.zero) && compCount < subjectCount) return 'lesser';
  return undefined;
};

const isCountDifferent = ({
  subjectValue,
  compValue,
}: {
  subjectValue: number | AloftArea;
  compValue: number | AloftArea;
}) => (subjectValue as number) !== (compValue as number);

type BathCountField = 'halfBaths' | 'fullBaths';
type BathAdjustmentKey = 'halfBaths' | 'fullBaths' | 'basementHalfBaths' | 'basementFullBaths';

const makeAboveGradeBathCheck = (
  label: string,
  field: BathCountField,
  adjustmentCellKeyId: BathAdjustmentKey,
): FeatureCheck => ({
  label,
  adjustmentCellKeyId,
  isMaterialDifference: isCountDifferent,
  getSubjectValue: (subject) => {
    const value = subject.improvements?.[field];
    return isDefined(value) ? value : undefined;
  },
  getCompValue: (comp) => {
    const value = comp.improvements?.[field];
    return isDefined(value) ? value : 0;
  },
  getAdjustment: (compAdjustments) =>
    orUndefined(schAloftPrice.parse)(compAdjustments[adjustmentCellKeyId]?.adjustment),
  getWrongDirectionComparison: getCountWrongDirectionComparison,
});

const makeBasementBathCheck = (
  label: string,
  field: BathCountField,
  adjustmentCellKeyId: BathAdjustmentKey,
): FeatureCheck => ({
  label,
  adjustmentCellKeyId,
  isMaterialDifference: isCountDifferent,
  getSubjectValue: (subject) => {
    const basement = subject.improvements?.basement;
    if (!basement || basement.hasFeature === false) return undefined;
    return isDefined(basement[field]) ? basement[field] : 0;
  },
  getCompValue: (comp) => {
    const basement = comp.improvements?.basement;
    if (!basement || basement.hasFeature === false) return 0;
    return isDefined(basement[field]) ? basement[field] : 0;
  },
  getAdjustment: (compAdjustments) =>
    orUndefined(schAloftPrice.parse)(compAdjustments[adjustmentCellKeyId]?.adjustment),
  getWrongDirectionComparison: getCountWrongDirectionComparison,
});

const getActualAgeWrongDirectionComparison: FeatureCheck['getWrongDirectionComparison'] = (
  adjustment,
  compValue,
  subjectValue,
) => {
  const compAge = compValue as number;
  const subjectAge = subjectValue as number;

  if (adjustment.gt(AloftPrice.zero) && compAge < subjectAge) return 'newer';
  if (adjustment.lt(AloftPrice.zero) && compAge > subjectAge) return 'older';
  return undefined;
};

const COMPARISON_MESSAGES: Record<'greater' | 'lesser', { message: string; tips: string }> = {
  greater: {
    message: 'greater',
    tips: 'greater',
  },
  lesser: {
    message: 'lesser',
    tips: 'lesser',
  },
};

const EXPECTED_ADJUSTMENT_DIRECTION: Record<FeatureComparison, string> = {
  greater: 'negative',
  lesser: 'positive',
  older: 'positive',
  newer: 'negative',
};

const buildDefaultAlertCopy = ({
  propertyName,
  comparison,
  adjustmentDirection,
  label,
}: AlertCopyContext) => {
  const comparisonMessage = COMPARISON_MESSAGES[comparison as 'greater' | 'lesser'];
  return {
    message: `${propertyName} has a ${comparisonMessage.message} ${label} than the Subject but a ${adjustmentDirection} adjustment is shown.`,
    tips: `Since the Comp is ${comparisonMessage.tips} in ${label}, a ${EXPECTED_ADJUSTMENT_DIRECTION[comparison]} adjustment is needed to normalize its value to the Subject. Alternatively, explain why the adjustment is justified.`,
  };
};

const actualAgeFeatureCheck: FeatureCheck = {
  label: 'Actual Age',
  adjustmentCellKeyId: 'actualAge',
  isMaterialDifference: isCountDifferent,
  getSubjectValue: (subject) => {
    const value = subject.improvements?.age;
    return isDefined(value) ? value : undefined;
  },
  getCompValue: (comp) => {
    const value = comp.improvements?.age;
    return isDefined(value) ? value : undefined;
  },
  getAdjustment: (compAdjustments) =>
    orUndefined(schAloftPrice.parse)(compAdjustments.actualAge?.adjustment),
  getWrongDirectionComparison: getActualAgeWrongDirectionComparison,
  buildAlertCopy: ({
    propertyName,
    comparison,
    adjustmentDirection,
    subjectValue,
    compValue,
  }) => ({
    message: `${propertyName} is ${comparison} in Actual Age but has a ${adjustmentDirection} adjustment (${propertyName}: ${compValue} yrs | Subject: ${subjectValue} yrs)`,
    tips: `Since the Comp is ${comparison}, a ${EXPECTED_ADJUSTMENT_DIRECTION[comparison]} adjustment is needed to normalize its value to the Subject. Alternatively, explain why the adjustment is justified.`,
  }),
};

const FEATURE_CHECKS: FeatureCheck[] = [
  glaFeatureCheck,
  siteSizeFeatureCheck,
  conditionFeatureCheck,
  qualityFeatureCheck,
  actualAgeFeatureCheck,
  makeAboveGradeBathCheck('Half Baths', 'halfBaths', 'halfBaths'),
  makeAboveGradeBathCheck('Full Baths', 'fullBaths', 'fullBaths'),
  makeBasementBathCheck('Basement Half Baths', 'halfBaths', 'basementHalfBaths'),
  makeBasementBathCheck('Basement Full Baths', 'fullBaths', 'basementFullBaths'),
];

const TARGET_CELL_TOOLTIP = 'Adjustment is in the wrong direction';

export const inconsistentCompAdjustment: AlertDefinition = ({
  compAdjustments,
  compSequence,
  fullCompSequence,
  compType,
  order,
  propertyDataEntities,
}) => {
  const subjectData = (propertyDataEntities.subject as SubjectPropertyDataState | undefined)
    ?.subjectData.editableData;

  if (!subjectData) return [];

  const scopedSubjectData = getSubjectDataForCompType(subjectData, compType);

  const locationType = orUndefined(schLocationType.parse)(
    subjectData.propertyIdentification?.locationType,
  );

  const fixRoute =
    schPairedCostRouteItem.enum[order.isInspectifyOrder ? 'adjustments-grid' : 'adjustments'];
  const triggers = getAlertTriggers({ fixRoute, order });

  return compSequence
    .flatMap((id) => {
      const propertyData = (propertyDataEntities[id] as CompPropertyDataState | undefined)?.compData
        .editableData;
      const compAdjustmentData = compAdjustments[id];
      if (!propertyData || !compAdjustmentData) return [];

      return FEATURE_CHECKS.flatMap((featureCheck) => {
        const subjectValue = featureCheck.getSubjectValue(scopedSubjectData);
        const compValue = featureCheck.getCompValue(propertyData);
        const adjustment = featureCheck.getAdjustment(compAdjustmentData);

        if (
          !isDefined(subjectValue) ||
          !isDefined(compValue) ||
          !isDefined(adjustment) ||
          adjustment.equals(AloftPrice.zero) ||
          !featureCheck.isMaterialDifference({ subjectValue, compValue, locationType })
        ) {
          return [];
        }

        const comparison = featureCheck.getWrongDirectionComparison(
          adjustment,
          compValue,
          subjectValue,
        );
        if (!comparison) return [];

        const propertyName = getComparableDisplayName(id, fullCompSequence ?? compSequence);
        const adjustmentDirection = adjustment.gt(AloftPrice.zero) ? 'positive' : 'negative';
        const targetCellKey = `${id}-${featureCheck.adjustmentCellKeyId}` as PropertyGridCellKey;
        const copyContext: AlertCopyContext = {
          propertyName,
          comparison,
          adjustmentDirection,
          subjectValue,
          compValue,
          label: featureCheck.label,
        };
        const copy = featureCheck.buildAlertCopy?.(copyContext) ?? buildDefaultAlertCopy(copyContext);

        return [
          {
            type: getScopedAlertType(schAlertType.enum.inconsistentCompAdjustment, compType),
            status: 'active' as const,
            severity: 'error' as const,
            orderType: ['appraisify'] as const,
            message: copy.message,
            propertyID: id,
            ...(compType ? { compType } : {}),
            fixingInfo: {
              tips: copy.tips,
              pairedCostRouteItem: fixRoute,
              fixingMethod: ['comment', 'action'] as const,
              commentAlwaysRequired: false,
              targetCellKey,
              targetCellTooltip: TARGET_CELL_TOOLTIP,
            },
            triggers,
          },
        ];
      });
    });
};
